/**
 * Light / dark theme (the demo's app.js theme toggle). The choice is remembered per device in localStorage
 * and applied as html[data-theme], which web/src/theme-dark.css styles. Like the demo, the app starts light
 * until the person chooses dark.
 */

export type Theme = 'light' | 'dark';

export const THEME_KEY = 'z3.theme';
const EVENT = 'z3-theme-change';

function saved(): Theme | null {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null; // private mode
  }
}

/** The theme in effect: the saved choice, else light. */
export function currentTheme(): Theme {
  return saved() ?? 'light';
}

/** Sets html[data-theme] (and the browser UI colour) without saving anything. */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#11151c' : '#22293a');
  window.dispatchEvent(new CustomEvent<Theme>(EVENT, { detail: theme }));
}

/** Saves the choice on this device and applies it. */
export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* private mode: the theme still applies until the page is closed */
  }
  applyTheme(theme);
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/** Call once before the app renders (main.tsx) so the first paint already uses the right theme. */
export function initTheme() {
  applyTheme(currentTheme());
  // Another tab changed the theme.
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_KEY) applyTheme(currentTheme());
  });
}

/** Subscribes to theme changes (used by the toggle button). Returns an unsubscribe function. */
export function onThemeChange(fn: (theme: Theme) => void) {
  const handler = (e: Event) => fn((e as CustomEvent<Theme>).detail);
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
